import React, {useCallback, useEffect, useRef} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import Grid from '@material-ui/core/Grid';
import {format, startOfDay, isBefore} from 'date-fns';
import {useStore} from 'effector-react';
import i18next from 'i18next';
import {Control, Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {IMaskMixin} from 'react-imask';
import {useHistory} from 'react-router-dom';
import * as yup from 'yup';

import {fetchDocumentUtilisationBatchNumber, fetchParticipantList, fetchStorages} from 'api';
import {ReactComponent as AutoBatchName} from 'assets/svg/auto-batch-name.svg';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {DatePicker} from 'components/ui-kit/DatePicker';
import {Icon} from 'components/ui-kit/Icon';
import {InputContainer, NativeInput} from 'components/ui-kit/Input/styled';
import {Label} from 'components/ui-kit/Label';
import {AsyncSelect} from 'components/ui-kit/Select';

import {ROUTE} from 'core/routes';
import {profile$} from 'models/auth';
import {
    dataChanged,
    stepChanged,
    utilisationForm$,
    utilisationFormChanged,
} from 'models/documents/create';
import {onSetEditParticipant} from 'models/profile/common';
import {colors} from 'theme';
import {UtilisationFormData} from 'types';

import {FormContainer, FormField, FormWrapper, NavigationButtonsContainer} from '../common/styled';

import {BatchAutoBanner} from './BatchAutoBanner';
import {DatePickerField, Hr} from './styled';

const BATCH_NUMBER_MASK = '00000000-000000';
const BATCH_NUMBER_MANUAL_REGEX = /^\d{8}-\d{6}$/;

const MaskedBatchNumberInput = IMaskMixin(
    ({
        inputRef,
        label,
        hasError,
        className,
        ...props
    }: {
        inputRef: React.Ref<HTMLInputElement>;
        label?: string;
        hasError?: boolean;
        className?: string;
        [key: string]: unknown;
    }) => (
        <InputContainer className={className}>
            <Label text={label} hasError={hasError}>
                <NativeInput
                    {...props}
                    ref={inputRef}
                    hasError={hasError}
                    hasAdornment={false}
                    className="wwm__native-input"
                />
            </Label>
        </InputContainer>
    ),
);

interface FormItemProps {
    control: Control;
    defaultValue?: string;
    name: string;
    setValue?: Function;
    fieldName?: string;
    getValues: Function;
    watch: Function;
}

export const StorageLocationId = ({
    control,
    setValue,
    getValues,
    watch,
    ...props
}: FormItemProps) => {
    const {t} = useTranslation();
    const productionLineId = watch('productionLineId');
    const profile = useStore(profile$);

    const NoStorageLocationSlot = () => {
        const history = useHistory();
        const [pending, setPending] = React.useState(false);
        const onRedirect = async () => {
            try {
                setPending(true);
                const participants = await fetchParticipantList();
                const user = participants.find((p) => p.id === profile?.client?.id);
                onSetEditParticipant(user || null);
                history.push(ROUTE.STORAGES_CREATE);
            } catch (error) {
                console.error(error);
            } finally {
                setPending(false);
            }
        };
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '10px',
                }}
            >
                Storage location is required
                <br />
                <Button
                    type="button"
                    variant={ButtonVariant.Outline}
                    onClick={onRedirect}
                    isLoading={pending}
                >
                    Go to profile and add it
                </Button>
            </div>
        );
    };

    const loadOptions = async () =>
        (await fetchStorages()).storageLocations.map((value) => ({
            value: value.id,
            label: value.storageName,
            isDisabled: value.status === 'deactivated',
            meta: {name: value.storageName ?? ''},
        }));

    return (
        <Controller
            control={control}
            {...props}
            render={({value, onChange}, {invalid}) => (
                <AsyncSelect
                    key={productionLineId}
                    label={t('pages.documents.form.storageLocationName')}
                    isClearable
                    defaultOptions
                    noOptionsMessage={() => <NoStorageLocationSlot />}
                    value={value ? {value, label: getValues(`storageLocationName`) || value} : null}
                    onChange={(val: {value: string; meta?: {name: string}}) => {
                        val ? onChange(val.value) : onChange(null);
                        setValue?.(`storageLocationName`, val?.meta?.name);
                    }}
                    loadOptions={loadOptions}
                    hasError={invalid}
                />
            )}
        />
    );
};

const schema = yup.object().shape({
    batchNumberSource: yup
        .mixed<'auto' | 'manual'>()
        .oneOf(['auto', 'manual'])
        .notRequired(),
    batchNumber: yup.string().when('batchNumberSource', {
        is: 'manual',
        then: (s) =>
            s
                .required(i18next.t('errors.batchNumber.required'))
                .matches(
                    BATCH_NUMBER_MANUAL_REGEX,
                    i18next.t('errors.batchNumber.invalidManualFormat'),
                ),
        otherwise: (s) => s.notRequired().nullable(),
    }),
    // factoryId: yup.string().required(i18next.t('components.form.required')),
    // productionLineId: yup.string().required(i18next.t('components.form.required')),
    storageLocationId: yup.string().required(i18next.t('components.form.required')),
    manufacturingDate: yup
        .date()
        .nullable()
        .required('components.form.required'),
    applicationDate: yup
        .date()
        .nullable()
        .required('components.form.required'),
});

export const DocumentForm = () => {
    const {t} = useTranslation();
    const utilisationForm = useStore(utilisationForm$);
    const batchNumberRef = useRef<string>('');
    useEffect(() => {
        batchNumberRef.current = utilisationForm.batchNumber || '';
    }, []);
    const {control, register, handleSubmit, formState, getValues, setValue, watch} = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        resolver: yupResolver(schema),
        defaultValues: {
            ...utilisationForm,
            batchNumberSource: utilisationForm.batchNumberSource ?? 'auto',
            manufacturingDate: utilisationForm.manufacturingDate
                ? startOfDay(new Date(utilisationForm.manufacturingDate))
                : null,
            applicationDate: utilisationForm.applicationDate
                ? startOfDay(new Date(utilisationForm.applicationDate))
                : null,
        },
    });
    useEffect(() => {
        register('batchNumberSource');
    }, [register]);

    const batchNumberSource = watch('batchNumberSource');

    const handleBatchNumberSourceChange = useCallback(
        (source: 'auto' | 'manual') => {
            utilisationFormChanged({
                ...utilisationForm,
                batchNumber: '',
                batchNumberSource: source,
            });
            setValue('batchNumberSource', source);
            setValue('batchNumber', '', {shouldValidate: true});
        },
        [setValue, utilisationForm],
    );

    const onHandleChanged = async (data: UtilisationFormData) => {
        if (batchNumberRef.current !== data.batchNumber) {
            dataChanged(null);
        }
        const dateISOPattern = "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'";
        const dates = {
            applicationDate: format(data.applicationDate as Date, dateISOPattern),
            manufacturingDate: format(data.manufacturingDate as Date, dateISOPattern),
        };

        if (data.batchNumberSource === 'auto') {
            if (utilisationForm.batchNumberAutoGeneratedHolder) {
                stepChanged(1);
                utilisationFormChanged({
                    ...data,
                    ...dates,
                    batchNumber: utilisationForm.batchNumberAutoGeneratedHolder,
                });
                return;
            }

            try {
                const productionDate = format(data.manufacturingDate as Date, 'yyyy-MM-dd');
                const batchNumber = await fetchDocumentUtilisationBatchNumber({
                    productionDate,
                });

                stepChanged(1);
                utilisationFormChanged({
                    ...data,
                    ...dates,
                    batchNumber,
                    batchNumberAutoGeneratedHolder: batchNumber,
                });
                return;
            } catch (error) {
                console.error(error);
            }
        }

        const normalizedBatchNumber = data.batchNumber?.trim();

        stepChanged(1);
        utilisationFormChanged({...data, ...dates, batchNumber: normalizedBatchNumber});
    };
    return (
        <FormContainer>
            <form onSubmit={handleSubmit(onHandleChanged)}>
                <FormWrapper>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <Button
                                variant={
                                    batchNumberSource === 'auto'
                                        ? ButtonVariant.Outline
                                        : ButtonVariant.OutlineSecondary
                                }
                                type="button"
                                onClick={() => handleBatchNumberSourceChange('manual')}
                            >
                                {t('pages.documents.details.Utilization.manualInput')}
                            </Button>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Button
                                variant={
                                    batchNumberSource === 'manual'
                                        ? ButtonVariant.Outline
                                        : ButtonVariant.OutlineSecondary
                                }
                                type="button"
                                onClick={() => handleBatchNumberSourceChange('auto')}
                            >
                                <Icon
                                    source={AutoBatchName}
                                    strokeColor={colors.brandPrimary}
                                    fillColor="transparent"
                                    width={16}
                                    height={16}
                                    style={{marginRight: 8}}
                                />
                                {t('pages.documents.details.Utilization.autoGenerate')}
                            </Button>
                        </Grid>
                        <Grid
                            style={{
                                display: batchNumberSource === 'manual' ? 'block' : 'none',
                            }}
                            item
                            xs={12}
                            md={8}
                        >
                            <FormField>
                                <div>
                                    <Controller
                                        control={control}
                                        name="batchNumber"
                                        render={({onChange, value}) => (
                                            <MaskedBatchNumberInput
                                                mask={BATCH_NUMBER_MASK}
                                                value={value || ''}
                                                onAccept={(maskedValue: string) =>
                                                    onChange(maskedValue)
                                                }
                                                placeholder={t(
                                                    'pages.documents.details.Utilization.batchNumberPlaceholder',
                                                )}
                                                label={t(
                                                    'pages.documents.details.Utilization.batchNumberLabel',
                                                )}
                                                hasError={!!formState.errors.batchNumber}
                                            />
                                        )}
                                    />
                                    {formState.errors.batchNumber && (
                                        <span style={{fontSize: 12, color: 'red'}}>
                                            {t(formState.errors.batchNumber.message as string)}
                                        </span>
                                    )}
                                </div>
                            </FormField>
                        </Grid>
                        {batchNumberSource === 'manual' ? (
                            <></>
                        ) : (
                            <Grid item xs={12} md={8}>
                                <BatchAutoBanner />
                            </Grid>
                        )}
                    </Grid>
                    <Hr />
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <FormField>
                                <DatePickerField>
                                    <Controller
                                        control={control}
                                        name="applicationDate"
                                        render={({onChange, value}) => (
                                            <DatePicker
                                                label={t(
                                                    'pages.documents.form.productApplicationLabel',
                                                )}
                                                maxDate={new Date()}
                                                value={value}
                                                onChange={(newDate: Date | null) => {
                                                    const {manufacturingDate} = control.getValues();
                                                    if (
                                                        newDate &&
                                                        manufacturingDate &&
                                                        typeof manufacturingDate !== 'string' &&
                                                        isBefore(newDate, manufacturingDate)
                                                    )
                                                        setValue('manufacturingDate', null);

                                                    onChange(newDate);
                                                }}
                                                hasError={!!formState.errors.applicationDate}
                                            />
                                        )}
                                    />
                                    {formState.errors.applicationDate && (
                                        <span style={{fontSize: 12, color: 'red'}}>
                                            {t(formState.errors.applicationDate.message as string)}
                                        </span>
                                    )}
                                </DatePickerField>
                            </FormField>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormField>
                                <DatePickerField>
                                    <Controller
                                        control={control}
                                        name="manufacturingDate"
                                        render={({onChange, value}) => {
                                            const applicationDate = watch('applicationDate');
                                            return (
                                                <DatePicker
                                                    label={t(
                                                        'pages.documents.form.productManufacturingLabel',
                                                    )}
                                                    maxDate={
                                                        typeof applicationDate !== 'string'
                                                            ? applicationDate
                                                            : undefined
                                                    }
                                                    value={value}
                                                    onChange={onChange}
                                                    hasError={!!formState.errors.manufacturingDate}
                                                />
                                            );
                                        }}
                                    />
                                    {formState.errors.manufacturingDate && (
                                        <span style={{fontSize: 12, color: 'red'}}>
                                            {t(
                                                formState.errors.manufacturingDate
                                                    .message as string,
                                            )}
                                        </span>
                                    )}
                                </DatePickerField>
                            </FormField>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormField>
                                <StorageLocationId
                                    defaultValue={''}
                                    control={control}
                                    name={`storageLocationId`}
                                    setValue={setValue}
                                    getValues={getValues}
                                    watch={watch}
                                />
                                <div style={{display: 'none'}}>
                                    <Controller
                                        defaultValue={''}
                                        control={control}
                                        name="storageLocationName"
                                        render={(props) => <input value={props.value} readOnly />}
                                    />
                                </div>
                            </FormField>
                        </Grid>
                    </Grid>
                </FormWrapper>
                <NavigationButtonsContainer>
                    <span />
                    <Button
                        style={{width: 'auto'}}
                        variant={ButtonVariant.Normal}
                        type="submit"
                        disabled={!formState.isValid}
                    >
                        {t('components.form.nextStep')}
                    </Button>
                </NavigationButtonsContainer>
            </form>
        </FormContainer>
    );
};
